import React, { useState, useEffect } from 'react';
import VehicleListing from '../components/Fleet/VehicleListing';
import VehicleCardView from '../components/Fleet/VehicleCardView';
import FleetSummaryCards from '../components/Fleet/FleetSummaryCards';
import FleetExportMenu from '../components/Fleet/FleetExportMenu';
import VehicleForm from '../components/Fleet/VehicleForm';
import VehicleDetailsModal from '../components/Fleet/VehicleDetailsModal';
import Modal from '../components/Common/Modal';
import { Vehicle, VehicleStatus, CreateVehicleDto, FleetSummary, VehicleAvailability } from '../types/vehicle';
import { vehicleService } from '../services/vehicleService';

const FleetManagement: React.FC = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [availability, setAvailability] = useState<VehicleAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadFleetData();
  }, [refreshKey]);

  const loadFleetData = async () => {
    try {
      setLoading(true);
      const [vehiclesResponse, summaryResponse, availabilityResponse] = await Promise.all([
        vehicleService.getAllVehicles(),
        vehicleService.getFleetSummary(),
        vehicleService.getVehiclesAvailability()
      ]);

      if (vehiclesResponse.success && vehiclesResponse.data) {
        setVehicles(vehiclesResponse.data);
      }

      if (summaryResponse.success && summaryResponse.data) {
        setSummary(summaryResponse.data);
      }

      if (availabilityResponse.success && availabilityResponse.data) {
        setAvailability(availabilityResponse.data);
      }
    } catch (err) {
      console.error('Failed to load fleet data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleVehicleSelect = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowViewModal(true);
  };

  const handleVehicleEdit = (vehicle: Vehicle) => {
    setShowViewModal(false); // Close view modal if open
    setEditingVehicle(vehicle);
    setShowEditModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedVehicle(null);
  };

  const handleVehicleDelete = (vehicleId: string) => {
    // TODO: Implement delete confirmation and API call
    console.log('Delete vehicle:', vehicleId);
  };

  const handleStatusUpdate = (vehicleId: string, status: VehicleStatus) => {
    console.log('Status updated:', vehicleId, status);
    handleRefresh();
  };

  const handleAddVehicle = () => {
    setShowAddModal(true);
  };

  const handleFormSubmit = async (vehicleData: CreateVehicleDto) => {
    setFormLoading(true);
    setError(null);

    try {
      let response;
      if (editingVehicle) {
        response = await vehicleService.updateVehicle(editingVehicle._id, vehicleData);
      } else {
        response = await vehicleService.createVehicle(vehicleData);
      }

      if (response.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditingVehicle(null);
        handleRefresh();
      } else {
        setError(response.error?.message || 'Failed to save vehicle');
      }
    } catch (err) {
      setError('Failed to save vehicle');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingVehicle(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Fleet Management</h1>
            <p className="text-blue-100 mt-1">Manage your vehicle fleet, track status, and schedule maintenance</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 flex gap-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-md transition-all ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white/20'
                }`}
                title="Table View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-2 rounded-md transition-all ${
                  viewMode === 'card'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white/20'
                }`}
                title="Card View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg">
              <FleetExportMenu vehicles={vehicles} />
            </div>

            <button
              onClick={handleAddVehicle}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium shadow-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Vehicle
            </button>

            <button
              onClick={handleRefresh}
              className="p-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all"
              disabled={loading}
              title="Refresh"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Fleet Summary Cards */}
      <FleetSummaryCards summary={summary} loading={loading} />

      {/* Content Area */}
      {viewMode === 'table' ? (
        <VehicleListing
          vehicles={vehicles}
          loading={loading}
          onVehicleSelect={handleVehicleSelect}
          onVehicleEdit={handleVehicleEdit}
          onVehicleDelete={handleVehicleDelete}
          onStatusUpdate={handleStatusUpdate}
          availability={availability}
        />
      ) : (
        <VehicleCardView
          vehicles={vehicles}
          onVehicleSelect={handleVehicleSelect}
          onVehicleEdit={handleVehicleEdit}
          loading={loading}
          availability={availability}
        />
      )}

      {/* Add Vehicle Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={handleFormCancel}
        size="xl"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6 mb-0">
            <div className="text-red-800">{error}</div>
          </div>
        )}
        <VehicleForm
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={formLoading}
        />
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={handleFormCancel}
        size="xl"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6 mb-0">
            <div className="text-red-800">{error}</div>
          </div>
        )}
        <VehicleForm
          vehicle={editingVehicle}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={formLoading}
        />
      </Modal>

      {/* View Vehicle Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={handleCloseViewModal}
        size="xl"
      >
        {selectedVehicle && (
          <VehicleDetailsModal
            vehicle={selectedVehicle}
            onClose={handleCloseViewModal}
            onEdit={handleVehicleEdit}
          />
        )}
      </Modal>
    </div>
  );
};

export default FleetManagement;
